/**
 * User Detail Panel Component
 * Displays detailed user information in a slide-in panel
 * Matches the Claude artifact HTML structure
 */

import React, { useEffect, useState, useCallback } from 'react';
import { apiService } from '../../services/apiService';
import { API_CONFIG } from '../../config/api';
import FreezeAccountModal from './FreezeAccountModal';
import UnfreezeAccountModal from './UnfreezeAccountModal';
import './UserDetailPanel.css';

interface UserDetailPanelProps {
  userId: string | null;
  isOpen: boolean;
  onClose: () => void;
  onUserUpdated?: () => void; // Called after freeze/unfreeze so parent can refresh the list
}

interface UserDetail {
  id: string;
  email: string;
  username: string;
  email_verified: boolean;
  kyc_status?: 'none' | 'pending' | 'approved' | 'rejected' | 'resubmission_required';
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  device_fingerprints: Array<{
    device_id: string;
    device_type: string;
    created_at: string;
  }>;
  ip_addresses: string[];
  frozen_at: string | null;
  freeze_reason: string | null;
  share_earn_brand?: string | null;
  share_earn_cohort?: string | null;
  [key: string]: any; // Allow additional properties from API
}

interface KYCImages {
  faceImageUrl: string | null;
  idFrontImageUrl: string | null;
  idBackImageUrl: string | null;
}

const UserDetailPanel: React.FC<UserDetailPanelProps> = ({ userId, isOpen, onClose, onUserUpdated }) => {
  const [user, setUser] = useState<UserDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showFreezeModal, setShowFreezeModal] = useState(false);
  const [showUnfreezeModal, setShowUnfreezeModal] = useState(false);
  const [kycImages, setKycImages] = useState<KYCImages | null>(null);
  const [seBrand, setSeBrand] = useState('');
  const [seCohort, setSeCohort] = useState('');
  const [seSaving, setSeSaving] = useState(false);

  // Define loadUserDetails before useEffect (arrow functions are not hoisted)
  const loadUserDetails = useCallback(async () => {
    if (!userId) return;
    
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.getUserById(userId);
      setUser(data.user);
      
      // Load KYC images if user is approved
      if (data.user.kyc_status === 'approved') {
        try {
          const kycData = await apiService.getKYCDetails(userId);
          setKycImages(kycData.images);
        } catch (kycErr) {
          // Silently fail if KYC details can't be loaded
          console.warn('Failed to load KYC images:', kycErr);
        }
      } else {
        setKycImages(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load user details');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (user) {
      setSeBrand(user.share_earn_brand ?? '');
      setSeCohort(user.share_earn_cohort ?? '');
    }
  }, [user]);

  // Load user details when panel opens
  useEffect(() => {
    if (isOpen && userId) {
      loadUserDetails();
    } else {
      // Reset state when panel closes
      setUser(null);
      setError(null);
      setShowFreezeModal(false);
      setShowUnfreezeModal(false);
    }
  }, [isOpen, userId, loadUserDetails]);

  const getInitials = (username: string): string => {
    return username
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getStatusText = (user: UserDetail): string => {
    if (user.frozen_at) return 'frozen';
    if (user.deleted_at) return 'deleted';
    if (!user.email_verified) return 'pending';
    return 'active';
  };

  const getStatusClass = (user: UserDetail): string => {
    if (user.frozen_at) return 'red';
    if (user.deleted_at) return 'red';
    if (!user.email_verified) return 'yellow';
    return 'green';
  };

  const getKYCStatus = (user: UserDetail): string => {
    if (user.deleted_at) return 'deleted';
    const kycStatus = user.kyc_status || 'none';
    if (kycStatus === 'approved') return 'verified';
    if (kycStatus === 'pending') return 'pending';
    if (kycStatus === 'rejected') return 'rejected';
    if (kycStatus === 'resubmission_required') return 'resubmission';
    return 'not verified'; // none
  };

  const getKYCClass = (user: UserDetail): string => {
    if (user.deleted_at) return 'red';
    const kycStatus = user.kyc_status || 'none';
    if (kycStatus === 'approved') return 'green';
    if (kycStatus === 'pending' || kycStatus === 'resubmission_required') return 'yellow';
    if (kycStatus === 'rejected') return 'red';
    return 'yellow'; // none
  };

  const getRiskScore = (): number => {
    // TODO: Calculate actual risk score from database
    return 0;
  };

  const getRiskClass = (score: number): string => {
    if (score >= 70) return 'high';
    if (score >= 40) return 'med';
    return 'low';
  };

  const handleFreeze = async (reason: string, notes: string) => {
    if (!userId) return;
    await apiService.freezeUser(userId, reason, notes);
    await loadUserDetails();
    onUserUpdated?.(); // Refresh parent list so frozen status shows correctly
  };

  const handleUnfreeze = async (reason: string, notes: string) => {
    if (!userId) return;
    await apiService.unfreezeUser(userId, reason, notes);
    await loadUserDetails();
    onUserUpdated?.(); // Refresh parent list so unfrozen status shows correctly
  };

  const handleSaveShareEarnTags = async () => {
    if (!userId) return;
    setSeSaving(true);
    setError(null);
    try {
      await apiService.patchUserShareEarnTags(userId, {
        shareEarnBrand: seBrand.trim() || null,
        shareEarnCohort: seCohort.trim() || null,
      });
      await loadUserDetails();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save Share & Earn tags');
    } finally {
      setSeSaving(false);
    }
  };

  const isFrozen = user?.frozen_at !== null && user?.frozen_at !== undefined;

  if (!isOpen) return null;

  const riskScore = getRiskScore();
  const riskClass = getRiskClass(riskScore);
  const statusText = user ? getStatusText(user) : '';
  const statusClass = user ? getStatusClass(user) : '';
  const kycStatus = user ? getKYCStatus(user) : '';
  const kycClass = user ? getKYCClass(user) : '';

  return (
    <>
      <div className={`detail-overlay ${isOpen ? 'open' : ''}`} onClick={onClose} />
      <div className={`detail-panel ${isOpen ? 'open' : ''}`}>
        <div className="detail-head">
          <span className="detail-title">User Details</span>
          <button className="detail-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="detail-body">
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
              Loading user details...
            </div>
          ) : error ? (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--red)' }}>
              Error: {error}
            </div>
          ) : user ? (
            <>
              <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                <div
                  className="user-av"
                  style={{
                    width: '80px',
                    height: '80px',
                    fontSize: '28px',
                    margin: '0 auto 12px',
                    background: 'var(--blue)',
                  }}
                >
                  {getInitials(user.username)}
                </div>
                <h3 style={{ fontSize: '18px', margin: '0 0 4px 0' }}>{user.username}</h3>
                <div style={{ color: 'var(--text-muted)' }}>@{user.username}</div>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '12px' }}>
                  <span className={`status ${statusClass}`}>{statusText}</span>
                  <span className={`status ${kycClass}`}>{kycStatus}</span>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '24px' }}>
                <div className={`risk ${riskClass}`} style={{ width: '80px', height: '80px', fontSize: '24px' }}>
                  {riskScore}
                </div>
              </div>

              <div className="detail-section">
                <div className="detail-label">👤 Account Information</div>
                <div className="detail-row">
                  <label>User ID</label>
                  <span className="mono">{user.id}</span>
                </div>
                <div className="detail-row">
                  <label>Email</label>
                  <span>{user.email}</span>
                </div>
                <div className="detail-row">
                  <label>Phone</label>
                  <span>-</span>
                </div>
                <div className="detail-row">
                  <label>Country</label>
                  <span>-</span>
                </div>
                <div className="detail-row">
                  <label>DOB</label>
                  <span>-</span>
                </div>
                <div className="detail-row">
                  <label>Registered</label>
                  <span>{new Date(user.created_at).toLocaleDateString()}</span>
                </div>
              </div>

              <div className="detail-section">
                <div className="detail-label">🤝 Share &amp; Earn (referrer tags)</div>
                <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '0 0 12px 0' }}>
                  Live campaign layering only: matches <code>brand_overrides</code> / <code>cohort_overrides</code> keys
                  in referral campaign config. Frozen share links ignore these fields.
                </p>
                <div className="detail-row">
                  <label htmlFor="se-brand">Brand tag</label>
                  <input
                    id="se-brand"
                    className="detail-input"
                    value={seBrand}
                    onChange={(e) => setSeBrand(e.target.value)}
                    placeholder="e.g. partner"
                    maxLength={64}
                    autoComplete="off"
                  />
                </div>
                <div className="detail-row">
                  <label htmlFor="se-cohort">Cohort tag</label>
                  <input
                    id="se-cohort"
                    className="detail-input"
                    value={seCohort}
                    onChange={(e) => setSeCohort(e.target.value)}
                    placeholder="e.g. vip"
                    maxLength={64}
                    autoComplete="off"
                  />
                </div>
                <div className="detail-row" style={{ marginTop: 8 }}>
                  <label />
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={seSaving}
                    onClick={handleSaveShareEarnTags}
                  >
                    {seSaving ? 'Saving…' : 'Save tags'}
                  </button>
                </div>
              </div>

              <div className="detail-section">
                <div className="detail-label">🪪 KYC Documents</div>
                {user.kyc_status === 'approved' && kycImages ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {kycImages.faceImageUrl && (
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>Face Photo</label>
                      <img 
                        src={kycImages.faceImageUrl.startsWith('http') ? kycImages.faceImageUrl : `${API_CONFIG.baseURL}${kycImages.faceImageUrl}`}
                        alt="Face" 
                        style={{ 
                          width: '100%', 
                          maxWidth: '300px', 
                          borderRadius: 'var(--radius-sm)',
                          border: '1px solid var(--glass-border)'
                        }} 
                      />
                    </div>
                  )}
                  {kycImages.idFrontImageUrl && (
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>ID Front</label>
                      <img 
                        src={kycImages.idFrontImageUrl.startsWith('http') ? kycImages.idFrontImageUrl : `${API_CONFIG.baseURL}${kycImages.idFrontImageUrl}`}
                        alt="Front of ID" 
                        style={{ 
                          width: '100%', 
                          maxWidth: '300px', 
                          borderRadius: 'var(--radius-sm)',
                          border: '1px solid var(--glass-border)'
                        }} 
                      />
                    </div>
                  )}
                  {kycImages.idBackImageUrl && (
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>ID Back</label>
                      <img 
                        src={kycImages.idBackImageUrl.startsWith('http') ? kycImages.idBackImageUrl : `${API_CONFIG.baseURL}${kycImages.idBackImageUrl}`}
                        alt="ID Back" 
                        style={{ 
                          width: '100%', 
                          maxWidth: '300px', 
                          borderRadius: 'var(--radius-sm)',
                          border: '1px solid var(--glass-border)'
                        }} 
                      />
                    </div>
                  )}
                  {!kycImages.faceImageUrl && !kycImages.idFrontImageUrl && !kycImages.idBackImageUrl && (
                    <div style={{ padding: '16px', background: 'var(--input)', borderRadius: 'var(--radius-sm)', color: 'var(--text-muted)', textAlign: 'center' }}>
                      No KYC images available
                    </div>
                  )}
                </div>
                ) : (
                  <div style={{ padding: '16px', background: 'var(--input)', borderRadius: 'var(--radius-sm)', color: 'var(--text-muted)', textAlign: 'center' }}>
                    {user.kyc_status === 'approved' ? 'KYC approved, but images not available' : 'No documents uploaded'}
                  </div>
                )}
              </div>

              <div className="detail-section">
                <div className="detail-label">💰 Financial Summary</div>
                <div className="detail-row">
                  <label>Total Deposits</label>
                  <span className="amount pos">£0</span>
                </div>
                <div className="detail-row">
                  <label>Total Withdrawals</label>
                  <span className="amount neg">£0</span>
                </div>
                <div className="detail-row">
                  <label>Net Revenue</label>
                  <span className="amount pos">£0</span>
                </div>
              </div>

              <div className="detail-section">
                <div className="detail-label">⚙️ Limits (Editable)</div>
                <div className="detail-row">
                  <label>Daily Deposit Limit</label>
                  <span>
                    £0 <button className="action" onClick={() => {/* TODO: Edit limit */}}>Edit</button>
                  </span>
                </div>
                <div className="detail-row">
                  <label>Monthly Loss Limit</label>
                  <span>
                    £0 <button className="action" onClick={() => {/* TODO: Edit limit */}}>Edit</button>
                  </span>
                </div>
              </div>

              <div className="detail-section">
                <div className="detail-label">📱 Devices & Payment Methods</div>
                <div className="detail-row">
                  <label>Devices</label>
                  <span style={{ fontSize: '12px' }}>
                    {user.device_fingerprints.length > 0
                      ? user.device_fingerprints.map((d) => d.device_type).join(', ')
                      : '-'}
                  </span>
                </div>
                <div className="detail-row">
                  <label>IP Addresses</label>
                  <span className="mono" style={{ fontSize: '11px' }}>
                    {user.ip_addresses && user.ip_addresses.length > 0
                      ? user.ip_addresses.join(', ')
                      : '-'}
                  </span>
                </div>
                <div className="detail-row">
                  <label>Payment Cards</label>
                  <span className="mono" style={{ fontSize: '11px' }}>None</span>
                </div>
              </div>
            </>
          ) : null}
        </div>

        <div className="detail-foot">
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
          <button className="btn btn-primary" onClick={() => {/* TODO: Edit user */}}>
            ✏️ Edit
          </button>
          {user && isFrozen ? (
            <button className="btn btn-success" onClick={() => setShowUnfreezeModal(true)}>
              🔓 Unfreeze
            </button>
          ) : (
            <button className="btn btn-danger" onClick={() => setShowFreezeModal(true)}>
              ❄️ Freeze
            </button>
          )}
        </div>
      </div>

      {user && (
        <>
          <FreezeAccountModal
            userId={user.id}
            userName={user.username}
            isOpen={showFreezeModal}
            onClose={() => setShowFreezeModal(false)}
            onConfirm={handleFreeze}
          />
          <UnfreezeAccountModal
            userId={user.id}
            userName={user.username}
            isOpen={showUnfreezeModal}
            onClose={() => setShowUnfreezeModal(false)}
            onConfirm={handleUnfreeze}
          />
        </>
      )}
    </>
  );
};

export default UserDetailPanel;

